import type { Rect, Vec2 } from "./magneticDockLayout";

/**
 * `viewportInsets` — how much of the 3D viewport is covered by floating UI, and
 * the lens shift that puts the scene in what is left.
 *
 * **The problem, in the user's words (2026-09-01):** during a workflow run the
 * scene is full-screen and the run's panels float over it. On a phone that is a
 * large share of a small screen — a side panel in landscape, a bottom panel in
 * portrait — so a model centred in the viewport is half behind a panel, and the
 * orbit pivot (which is what rotation appears to happen about) sits under it
 * too. The ask: "offset the view to the side so content is visible and centred
 * within this area, and orbit centre is also offsetted, while scene still
 * visible behind overlayed workflow panel".
 *
 * **Web-first.** The desktop client's panels are anchored beside the viewport
 * rather than over it, so there is no Unity counterpart to port; this is built
 * from Campfire's own conventions. The nearest prior art anywhere is a mapping
 * SDK's `setPadding` — same idea, same reason.
 *
 * **Headless on purpose.** No `three`, no Vue, no DOM — it takes rectangles and
 * returns numbers, so the whole decision is drivable in a harness. The two
 * halves that are NOT here: who supplies the rectangles
 * (`viewmodels/useSceneViewportInsets.ts`, which owns the policy of *which*
 * panels count and derives the camera and control answers from one sample) and
 * what is done with the camera answer
 * (`OrbitCameraController.setViewportInsets`, which owns the `three` call).
 */

/**
 * Pixels of the viewport covered on each edge, i.e. the padding that has to be
 * left clear. `{0,0,0,0}` means the whole viewport is usable.
 *
 * Edges rather than a free `Rect` because that is what the four inputs
 * naturally are and what the offset is derived from — and because two opposing
 * insets carry information a rect does not: a panel on the right and one on the
 * left cancel, and the scene stays centred, which is correct.
 */
export interface ViewportInsets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export const ZERO_VIEWPORT_INSETS: ViewportInsets = { top: 0, right: 0, bottom: 0, left: 0 };

export interface ViewportInsetOptions {
  /**
   * The largest share of one dimension a single edge may claim.
   *
   * A panel dragged over the middle of the screen cannot be cleared by
   * shrinking one edge — every candidate costs most of the viewport — so this
   * bounds how far the attempt goes rather than letting one occluder swing the
   * camera across the screen.
   */
  readonly maxEdgeFraction: number;
  /**
   * Below this share of a dimension an occluder contributes nothing.
   *
   * A dead zone, not an optimization: without it a 2px sliver of overlap from a
   * panel that is almost off-canvas produces a small, twitchy offset that reads
   * as the camera drifting for no reason.
   */
  readonly minEdgeFraction: number;
  /**
   * The free rect keeps at least this share of each dimension. When the insets
   * on one axis would leave less, BOTH are scaled down proportionally.
   *
   * Proportional rather than clamped-independently because scaling preserves
   * the free rect's CENTRE line: a crowded screen gets a smaller shift rather
   * than a shift in a different direction, which is the graceful degradation.
   */
  readonly minFreeFraction: number;
  /**
   * An occluder covering more of the viewport AREA than this is ignored
   * entirely.
   *
   * "The whole screen is covered" is not an occlusion you can steer around, and
   * the case is real: a media-only step expands its panel to fill the screen.
   * Without this the clamps above still produce a large offset, so leaving that
   * step would swing the camera back for a view nobody could see anyway.
   */
  readonly maxCoverFraction: number;
}

export const DEFAULT_VIEWPORT_INSET_OPTIONS: ViewportInsetOptions = {
  maxEdgeFraction: 0.5,
  minEdgeFraction: 0.04,
  minFreeFraction: 0.35,
  maxCoverFraction: 0.8,
};

type Edge = "top" | "right" | "bottom" | "left";
type EdgeLengths = Readonly<Record<Edge, number>>;
type EdgeSelector = (
  viewport: Rect,
  clipped: Rect,
  need: EdgeLengths,
  dimension: EdgeLengths,
) => Edge;

/**
 * Fixed evaluation order, so a tie between two equally cheap edges resolves the
 * same way every frame. Ties are rare (they need an occluder centred on a
 * diagonal) but a non-deterministic winner would make the camera flip between
 * two offsets on sub-pixel movement.
 */
const K_EDGE_ORDER: readonly Edge[] = ["bottom", "top", "right", "left"];

/**
 * The insets that clear `occluders` out of `viewport`, all in the same
 * coordinate space (client pixels, in practice).
 *
 * Each occluder is cleared by shrinking ONE edge — whichever costs the smallest
 * FRACTION of its own dimension. The fraction is what makes it right: a panel
 * in the bottom-right corner of a 852x393 landscape phone costs 378px to clear
 * to the right and 262px to clear downward, and the pixel-cheapest answer
 * (bottom, 67% of the height) is far worse than the fraction-cheapest one
 * (right, 44% of the width). Contributions combine with `max` per edge, so a
 * column of panels stacked on one edge costs that edge once.
 */
export function computeViewportInsets(
  viewport: Rect,
  occluders: readonly Rect[],
  options: ViewportInsetOptions = DEFAULT_VIEWPORT_INSET_OPTIONS,
): ViewportInsets {
  return computeInsets(viewport, occluders, options, selectCheapestEdge);
}

/**
 * Side-first free-area insets for consumers that must keep a side panel on its
 * horizontal edge regardless of that panel's height.
 *
 * A panel wholly on one side of the viewport centre line is side chrome, so it
 * always claims that HORIZONTAL edge — however tall it is. Full-screen video
 * controls always use this policy so they stay centred in the entire strip
 * beside the workflow panel. The scene camera uses it only on a landscape phone:
 * there, choosing a short panel's top or bottom edge produced a tiny vertical
 * shift that read as no offset at all (user direction, 2026-09-04).
 *
 * A panel crossing the centre line is not "on the side"; it falls back to the
 * general cheapest-edge policy, including the same dead-zone, cover and
 * free-area guards.
 */
export function computeSideFirstViewportInsets(
  viewport: Rect,
  occluders: readonly Rect[],
  options: ViewportInsetOptions = DEFAULT_VIEWPORT_INSET_OPTIONS,
): ViewportInsets {
  return computeInsets(viewport, occluders, options, selectSideFirstEdge);
}

function computeInsets(
  viewport: Rect,
  occluders: readonly Rect[],
  options: ViewportInsetOptions,
  selectEdge: EdgeSelector,
): ViewportInsets {
  const width = viewport.width;
  const height = viewport.height;
  if (width <= 0 || height <= 0) return ZERO_VIEWPORT_INSETS;

  const viewportArea = width * height;
  let top = 0;
  let right = 0;
  let bottom = 0;
  let left = 0;

  for (const occluder of occluders) {
    const clipped = intersect(viewport, occluder);
    if (clipped === null) continue;
    if (clipped.width * clipped.height > viewportArea * options.maxCoverFraction) continue;

    const need: EdgeLengths = {
      left: clipped.x + clipped.width - viewport.x,
      right: viewport.x + width - clipped.x,
      top: clipped.y + clipped.height - viewport.y,
      bottom: viewport.y + height - clipped.y,
    };
    const dimension: EdgeLengths = {
      left: width,
      right: width,
      top: height,
      bottom: height,
    };

    const bestEdge = selectEdge(viewport, clipped, need, dimension);
    const bestFraction = need[bestEdge] / dimension[bestEdge];
    if (bestFraction < options.minEdgeFraction) continue;

    const capped = Math.min(need[bestEdge], dimension[bestEdge] * options.maxEdgeFraction);
    if (bestEdge === "top") top = Math.max(top, capped);
    else if (bestEdge === "right") right = Math.max(right, capped);
    else if (bestEdge === "bottom") bottom = Math.max(bottom, capped);
    else left = Math.max(left, capped);
  }

  const horizontal = keepFree(left, right, width, options.minFreeFraction);
  const vertical = keepFree(top, bottom, height, options.minFreeFraction);
  return {
    top: vertical.a,
    right: horizontal.b,
    bottom: vertical.b,
    left: horizontal.a,
  };
}

function selectCheapestEdge(
  _viewport: Rect,
  _clipped: Rect,
  need: EdgeLengths,
  dimension: EdgeLengths,
): Edge {
  let bestEdge: Edge = "bottom";
  let bestFraction = Number.POSITIVE_INFINITY;
  for (const edge of K_EDGE_ORDER) {
    const fraction = need[edge] / dimension[edge];
    if (fraction < bestFraction) {
      bestFraction = fraction;
      bestEdge = edge;
    }
  }
  return bestEdge;
}

function selectSideFirstEdge(
  viewport: Rect,
  clipped: Rect,
  need: EdgeLengths,
  dimension: EdgeLengths,
): Edge {
  const centerX = viewport.x + viewport.width / 2;
  if (clipped.x >= centerX) return "right";
  if (clipped.x + clipped.width <= centerX) return "left";
  return selectCheapestEdge(viewport, clipped, need, dimension);
}

/**
 * The lens shift, in pixels, that moves the scene's optical axis toward the free
 * rect's centre.
 *
 * Handed straight to `PerspectiveCamera.setViewOffset(W, H, x, y, W, H)`, whose
 * `offsetX` shifts the frustum window right — which moves the rendered CONTENT
 * left by that many pixels. So a panel on the RIGHT (a positive `right` inset)
 * yields a positive x, and the scene slides left into the free area. Same
 * reasoning vertically: a bottom panel yields a positive y and the scene rises.
 *
 * Half the difference and not the difference: moving the CENTRE by half the
 * imbalance is what would put it exactly on the free rect's centre line, and it
 * is why two opposing panels cancel however large they are.
 *
 * `overlapFraction` is the share of that move the caller DECLINES, so the
 * content is left overlapping the panel by that much. It is a property of the
 * CONSUMER rather than of the measurement — a 3D scene renders through
 * translucent glass, so a little overlap costs nothing and a full clearance
 * throws the model at the far edge (user-reported 2026-09-01: "that smart offset
 * moving scene away too far. reduce to allow 20% overlap"), while a BUTTON under
 * a panel is invisible and unclickable. That is why it is a parameter here and
 * not a term inside either inset policy: the camera's chosen insets stay the
 * honest answer to "what is covered" before only the scene applies a tolerance
 * to them. Video controls always derive side-first insets from the same rectangle
 * sample, and the camera does too on a landscape phone; see
 * {@link computeSideFirstViewportInsets}.
 */
export function viewOffsetFromInsets(insets: ViewportInsets, overlapFraction = 0): Vec2 {
  const keep = 1 - Math.max(0, Math.min(1, overlapFraction));
  return {
    x: ((insets.right - insets.left) / 2) * keep,
    y: ((insets.bottom - insets.top) / 2) * keep,
  };
}

/** True when no edge differs by more than `epsilon` — the change gate. */
export function viewportInsetsEqual(
  a: ViewportInsets,
  b: ViewportInsets,
  epsilon = 0.5,
): boolean {
  return (
    Math.abs(a.top - b.top) <= epsilon
    && Math.abs(a.right - b.right) <= epsilon
    && Math.abs(a.bottom - b.bottom) <= epsilon
    && Math.abs(a.left - b.left) <= epsilon
  );
}

/**
 * Move `current` a fraction `t` of the way to `target`, per edge.
 *
 * The caller derives `t` from the frame's delta (`1 - exp(-dt / tau)`), which
 * is what makes the approach frame-rate independent and lets it track a MOVING
 * target — a panel being dragged — without ever overshooting it.
 */
export function approachViewportInsets(
  current: ViewportInsets,
  target: ViewportInsets,
  t: number,
): ViewportInsets {
  const k = Math.max(0, Math.min(1, t));
  return {
    top: current.top + (target.top - current.top) * k,
    right: current.right + (target.right - current.right) * k,
    bottom: current.bottom + (target.bottom - current.bottom) * k,
    left: current.left + (target.left - current.left) * k,
  };
}

/**
 * The four custom properties a DOM surface reads to place itself in the free
 * area, in the order `inset`'s shorthand takes them.
 *
 * ⚠ They live here, beside the geometry, because they are a CONTRACT WITH
 * STYLESHEETS: the theater rules in `ProjectView` and the free-area image band
 * in `WorkflowMediaView` (`.wfm-anchor--free-area`) read these names, and a
 * publisher that spelled them differently — or wrote bare numbers where a
 * `calc()` needs a length — would fail silently, since an unresolvable `var()`
 * makes the whole declaration invalid at computed-value time and `inset` falls
 * back to `auto` rather than to the rule it was overriding.
 */
export const VIEWPORT_INSET_CSS_PROPERTIES = [
  ["--cf-viewport-inset-top", "top"],
  ["--cf-viewport-inset-right", "right"],
  ["--cf-viewport-inset-bottom", "bottom"],
  ["--cf-viewport-inset-left", "left"],
] as const;

/**
 * The subset of `CSSStyleDeclaration` {@link writeViewportInsets} needs.
 *
 * Structural rather than the DOM type so this module stays headless and the
 * write is drivable in a harness — the same reason `computeViewportInsets`
 * takes rectangles rather than elements.
 */
export interface StylePropertyTarget {
  setProperty(property: string, value: string): void;
  removeProperty(property: string): void;
}

/**
 * Mirror `insets` onto a style declaration, or REMOVE them when it is null so
 * the consumer's `0px` fallback applies and the surface degrades to the layout
 * it had before any of this existed.
 *
 * Written as px LENGTHS, not bare numbers: every consumer is a `calc()` in a
 * stylesheet. (The bare-number form this codebase also uses —
 * `--cf-wfp-anchor-x` — is the one that is parsed back in JS.)
 */
export function writeViewportInsets(
  target: StylePropertyTarget,
  insets: ViewportInsets | null,
): void {
  for (const [property, edge] of VIEWPORT_INSET_CSS_PROPERTIES) {
    if (insets === null) target.removeProperty(property);
    else target.setProperty(property, `${insets[edge]}px`);
  }
}

/** The overlap of two rects, or null when they do not touch. */
function intersect(a: Rect, b: Rect): Rect | null {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const right = Math.min(a.x + a.width, b.x + b.width);
  const bottom = Math.min(a.y + a.height, b.y + b.height);
  if (right <= x || bottom <= y) return null;
  return { x, y, width: right - x, height: bottom - y };
}

/**
 * Scale an opposing pair down together until they leave `minFree` of `size`.
 *
 * Together rather than individually: shrinking both by the same factor shrinks
 * their DIFFERENCE by that factor too, so the free rect's centre — and
 * therefore the direction and proportion of the shift — is preserved.
 */
function keepFree(
  a: number,
  b: number,
  size: number,
  minFree: number,
): { a: number; b: number } {
  const allowed = size * (1 - minFree);
  const total = a + b;
  if (total <= allowed || total <= 0) return { a, b };
  const scale = allowed / total;
  return { a: a * scale, b: b * scale };
}
